<template>
    <div class="timeline-kanban">
        <div class="board-header">
            <div>
                <p class="eyebrow">Línea de tiempo</p>
                <h2 class="board-title">Eventos del paciente</h2>
                <p class="board-subtitle">Arrastra para cambiar categoría y reordenar</p>
            </div>
        </div>

        <div class="kanban-board">
            <div
                v-for="column in columns"
                :key="column.id"
                class="kanban-column"
                :class="{ 'is-active': dragOverColumn === column.id }"
                :data-col="column.id"
                @dragover.prevent="onDragOver($event, column.id)"
                @dragleave="onDragLeave($event, column.id)"
                @drop="onDrop($event, column.id)"
            >
                <div class="column-header">
                    <div class="header-content">
                        <i :class="column.icon" class="column-icon"></i>
                        <h3 class="column-title">{{ column.title }}</h3>
                        <span class="column-count">{{ getItemsInColumn(column.id).length }}</span>
                    </div>
                </div>
                
                <div class="column-body p-2" :class="{ 'drag-over': dragOverColumn === column.id }">
                    <!-- Indicador de drop al inicio -->
                    <div 
                        v-if="dropIndicator.show && dropIndicator.columnId === column.id && dropIndicator.index === 0"
                        class="drop-indicator"
                    >
                        <div class="drop-line"></div>
                        <div class="drop-text">Soltar aquí</div>
                    </div>
                    
                    <template v-for="(item, index) in getItemsInColumn(column.id)" :key="`${item.tipo}-${item.id}`">
                        <div
                            class="kanban-card"
                            :class="{ 
                                'dragging': draggedItem && draggedItem.id === item.id && draggedItem.tipo === item.tipo
                            }"
                            draggable="true"
                            @dragstart="onDragStart($event, item, column.id, index)"
                            @dragend="onDragEnd"
                            @dragenter="onDragEnterCard($event, item, column.id, index)"
                            @dragover.prevent
                        >
                            <div class="drag-handle" aria-hidden="true">
                                <i class="fas fa-grip-lines"></i>
                            </div>
                            <div class="card-header p-2">
                                <span class="card-type" :class="`type-${item.tipo}`">
                                    <i :class="getTypeIcon(item.tipo)"></i>
                                    <span class="type-label">{{ mapTipo(item.tipo) }}</span>
                                </span>
                                <div class="card-actions">
                                    <button class="action-btn" title="Editar" @click.stop="editItem(item)">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                    <button class="action-btn" title="Eliminar" @click.stop="deleteItem(item)">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                            
                            <div class="card-body p-0">
                                <p v-if="item.resumen" class="card-summary">{{ item.resumen }}</p>
                                <p v-else class="card-empty">Sin descripción</p>
                            </div>
                            
                            <div class="card-footer p-2">
                                <span class="card-date">
                                    <i class="far fa-calendar-alt"></i>
                                    {{ formatFullDate(item.created_at) }}
                                </span>
                            </div>
                        </div>
                        
                        <!-- Indicador de drop después de cada card -->
                        <div 
                            v-if="dropIndicator.show && dropIndicator.columnId === column.id && dropIndicator.index === index + 1"
                            class="drop-indicator"
                        >
                            <div class="drop-line"></div>
                            <div class="drop-text">Soltar aquí</div>
                        </div>
                    </template>
                    
                    <div v-if="getItemsInColumn(column.id).length === 0" class="empty-column">
                        <i class="fas fa-inbox"></i>
                        <p>Arrastra elementos aquí</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import api from '@/services/api';

const debugEnabled = false;
const debugLog = (...args) => {
    if (debugEnabled) console.log(...args);
};
const debugWarn = (...args) => {
    if (debugEnabled) console.warn(...args);
};

const props = defineProps({
    items: {
        type: Array,
        required: true
    },
    patientId: {
        type: [Number, String],
        required: true
    }
});

const emit = defineEmits(['item-moved', 'edit-item', 'delete-item', 'refresh']);

// Estado local para manipular orden sin mutar props
const localItems = ref([]);
const isDragging = ref(false);
const storageKey = computed(() => `timeline-order-${props.patientId}`);

// Definición de columnas (debe estar ANTES del watch para evitar ReferenceError)
const columns = [
    {
        id: 'general-cases',
        title: 'Casos generales',
        icon: 'fas fa-file-medical'
    },
    {
        id: 'clinical-followup',
        title: 'Seguimiento clínico',
        icon: 'fas fa-stethoscope'
    },
    {
        id: 'urgent-cases',
        title: 'Casos urgentes',
        icon: 'fas fa-exclamation-triangle'
    },
    {
        id: 'special-cases',
        title: 'Casos especiales',
        icon: 'fas fa-star'
    },
    {
        id: 'fixed-findings',
        title: 'Hallazgos fijos',
        icon: 'fas fa-thumbtack'
    }
];

// Normaliza órdenes por columna en base 1 y corrige vacíos/duplicados
const normalizeOrders = (items) => {
    if (!items || items.length === 0) {
        debugLog('⚠️  normalizeOrders: items vacío o undefined');
        return [];
    }
    
    debugLog('🔧 normalizeOrders: procesando', items.length, 'items');
    
    const byColumn = items.reduce((acc, item) => {
        const key = item.category || 'general-cases';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    debugLog('📊 Items por columna:', Object.entries(byColumn).map(([col, items]) => 
        `${col}: ${items.length}`
    ).join(', '));

    const normalized = [];
    Object.keys(byColumn).forEach((col) => {
        const sorted = byColumn[col]
            .slice()
            .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

        sorted.forEach((item, idx) => {
            const orderValue = idx + 1;
            normalized.push({
                ...item,
                order: orderValue,
                category: col,
                raw: item.raw ? { ...item.raw, order: orderValue, category: col } : item.raw
            });
        });
    });

    debugLog('✅ normalizeOrders resultado:', normalized.length, 'items');
    return normalized;
};

// Aplica un orden previamente guardado en localStorage (por patientId)
const applyStoredOrder = (items) => {
    try {
        const cached = localStorage.getItem(storageKey.value);
        debugLog('📂 localStorage cache:', cached ? 'encontrado' : 'no existe');
        
        if (!cached) {
            debugLog('💾 No hay cache, retornando items normalizados');
            return items;
        }
        
        const parsed = JSON.parse(cached);
        if (!parsed || typeof parsed !== 'object') {
            debugLog('⚠️  Cache inválido');
            return items;
        }

        const orderMap = parsed || {};
        debugLog('📋 Items en cache:', Object.keys(orderMap).length);
        
        const withStored = items.map((item) => {
            const key = `${item.tipo}-${item.id}`;
            const stored = orderMap[key];
            if (stored) {
                debugLog(`✅ Aplicando orden a ${key}:`, stored);
                return {
                    ...item,
                    category: stored.category || item.category,
                    order: stored.order || item.order,
                    raw: item.raw
                        ? { ...item.raw, category: stored.category || item.raw.category, order: stored.order || item.raw.order }
                        : item.raw
                };
            }
            debugLog(`ℹ️  Sin cache para ${key}, usando defaults`);
            return item;
        });
        
        const result = normalizeOrders(withStored);
        debugLog('🎯 Resultado final applyStoredOrder:', result.length, 'items');
        return result;
    } catch (error) {
        console.error('❌ Error aplicando orden local:', error);
        return items;
    }
};

// Sincronizar items locales y normalizar orden (sin mutar props)
watch(
    () => props.items,
    (newItems) => {
        if (isDragging.value) return; // evitar sobrescribir mientras arrastramos

        if (!newItems || newItems.length === 0) {
            localItems.value = [];
            return;
        }
        
        // Si ya tenemos items locales, hacer merge inteligente
        if (localItems.value.length > 0) {
            // Crear mapa de items existentes con su orden actual
            const existingMap = new Map();
            localItems.value.forEach(item => {
                const key = `${item.tipo}-${item.id}`;
                existingMap.set(key, { category: item.category, order: item.order });
            });
            
            // Aplicar orden existente a items nuevos
            const merged = newItems.map(item => {
                const key = `${item.tipo}-${item.id}`;
                const existing = existingMap.get(key);
                
                if (existing) {
                    // Item existente: preservar categoría y orden local
                    return {
                        ...item,
                        category: existing.category,
                        order: existing.order,
                        raw: item.raw ? { ...item.raw, category: existing.category, order: existing.order } : item.raw
                    };
                } else {
                    // Item nuevo: usar datos del backend o defaults
                    return {
                        ...item,
                        category: item.category || 'general-cases',
                        order: item.order || null,
                        raw: item.raw ? { ...item.raw } : item.raw
                    };
                }
            });
            
            const normalized = normalizeOrders(merged);
            localItems.value = normalized;
            
        } else {
            // Primera carga: aplicar orden desde localStorage o backend
            const cloned = newItems.map(item => ({
                ...item,
                category: item.category || 'general-cases',
                order: item.order || null,
                raw: item.raw ? { ...item.raw } : item.raw
            }));
            
            const normalized = normalizeOrders(cloned);
            const withStoredOrder = applyStoredOrder(normalized);
            
            localItems.value = withStoredOrder;
        }
    },
    { immediate: true, deep: true }
);

// Estado del drag & drop
const draggedItem = ref(null);
const draggedItemColumn = ref(null);
const draggedItemIndex = ref(null);
const dragOverColumn = ref(null);
const dropIndicator = ref({
    show: false,
    columnId: null,
    index: null
});
const lastIndicatorUpdate = ref(0); // Timestamp para evitar actualizaciones muy frecuentes

// Controla el indicador para evitar renders excesivos y parpadeos
const setDropIndicator = (columnId, index) => {
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    const sameTarget = dropIndicator.value.show &&
        dropIndicator.value.columnId === columnId &&
        dropIndicator.value.index === index;

    // Evitar renders innecesarios o muy seguidos
    if (sameTarget || now - lastIndicatorUpdate.value < 40) {
        return;
    }

    lastIndicatorUpdate.value = now;
    dropIndicator.value = {
        show: true,
        columnId,
        index
    };
};

// Asegura órdenes consecutivos en memoria antes de re-renderizar o mandar al backend
const applySequentialOrder = (list, columnId) => {
    return list.map((item, idx) => {
        const orderValue = idx + 1;
        item.order = orderValue;
        item.category = columnId;
        if (item.raw) {
            item.raw.order = orderValue;
            item.raw.category = columnId;
        }
        return item;
    });
};

// Obtener items por columna ordenados por order
const getItemsInColumn = (columnId) => {
    return localItems.value
        .filter(item => (item.category || 'general-cases') === columnId)
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
};

// Handlers de drag & drop
const onDragStart = (event, item, columnId, index) => {
    isDragging.value = true;
    draggedItem.value = item;
    draggedItemColumn.value = columnId;
    draggedItemIndex.value = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', '');
    
    // Ocultar indicador al empezar a arrastrar
    dropIndicator.value = { show: false, columnId: null, index: null };
};

const onDragEnd = () => {
    draggedItem.value = null;
    draggedItemColumn.value = null;
    draggedItemIndex.value = null;
    dragOverColumn.value = null;
    dropIndicator.value = { show: false, columnId: null, index: null };
    
    // Reactivar sincronización después de un pequeño delay
    setTimeout(() => {
        isDragging.value = false;
        persistOrderSnapshot();
    }, 100);
};

const onDragEnterCard = (event, item, columnId, index) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!draggedItem.value) return;
    
    // No actualizar si es la misma tarjeta
    if (draggedItem.value.id === item.id && draggedItem.value.tipo === item.tipo) {
        return;
    }
    
    // Determinar si insertar antes o después basado en la posición del mouse
    const cardElement = event.target.closest('.kanban-card');
    if (!cardElement) return;
    
    const rect = cardElement.getBoundingClientRect();
    const mouseY = event.clientY;
    const cardMiddle = rect.top + rect.height / 2;
    
    let targetIndex;
    if (mouseY < cardMiddle) {
        // Insertar antes de esta card
        targetIndex = index;
    } else {
        // Insertar después de esta card
        targetIndex = index + 1;
    }
    
    // Siempre mantener el indicador visible, solo cambiar la posición
    setDropIndicator(columnId, targetIndex);
};

const onDragOver = (event, columnId) => {
    event.preventDefault();
    dragOverColumn.value = columnId;

    if (!draggedItem.value) return;

    // Si estamos sobre una tarjeta, dejar que onDragEnterCard maneje el indicador
    const overCard = event.target.closest('.kanban-card');
    if (overCard) return;

    // Si no estamos sobre una tarjeta, mostrar indicador al final de la columna
    const itemsInColumn = getItemsInColumn(columnId);
    
    // Mantener el indicador siempre visible al final
    setDropIndicator(columnId, itemsInColumn.length);
};

const onDragLeave = (event, columnId) => {
    // No ocultar el indicador cuando salimos de la columna
    // Dejar que onDragEnter de la siguiente columna lo actualice
    const relatedTarget = event.relatedTarget;
    if (!relatedTarget) return;
    
    const columnElement = event.currentTarget;
    if (!columnElement.contains(relatedTarget)) {
        if (dragOverColumn.value === columnId) {
            dragOverColumn.value = null;
            // El indicador se mantiene visible hasta que entre a otra columna
        }
    }
};

const onDrop = (event, columnId) => {
    event.preventDefault();
    
    if (!draggedItem.value) return;
    
    const sourceItem = draggedItem.value;
    const sourceColumnId = draggedItemColumn.value;
    const indicatorActive = dropIndicator.value.show && dropIndicator.value.columnId === columnId;
    const targetIndex = indicatorActive ? dropIndicator.value.index : calculateDropIndex(event, columnId);
    
    debugLog('⬇️ onDrop:', { 
        sourceColumnId, 
        columnId, 
        targetIndex, 
        indicatorActive,
        itemId: sourceItem.id 
    });
    
    // Limpiar indicador
    dropIndicator.value = { show: false, columnId: null, index: null };
    dragOverColumn.value = null;
    
    // Determinar si se mueve a otra columna o se reordena en la misma
    if (sourceColumnId === columnId) {
        // Reordenar dentro de la misma columna
        reorderInSameColumn(sourceItem, columnId, targetIndex);
    } else {
        // Mover a otra columna
        moveToColumn(sourceItem, sourceColumnId, columnId, targetIndex);
    }

    onDragEnd();
};

// Reordenar dentro de la misma columna
const reorderInSameColumn = async (item, columnId, toIndex) => {
    const itemsInColumn = getItemsInColumn(columnId);
    const currentIndex = itemsInColumn.findIndex(
        (current) => current.id === item.id && current.tipo === item.tipo
    );

    if (currentIndex === -1) {
        debugWarn('❌ Item no encontrado en columna', { item, columnId });
        return;
    }

    // Validar targetIndex
    const maxIndex = itemsInColumn.length - 1;
    const validTargetIndex = Math.min(Math.max(toIndex, 0), maxIndex);

    debugLog('📍 Reordenando:', { 
        currentIndex, 
        targetIndex: toIndex, 
        validTargetIndex,
        itemId: item.id 
    });

    // Si no cambia la posición, no hacer nada
    if (validTargetIndex === currentIndex) {
        debugLog('⏭️ Misma posición, no reordenar');
        return;
    }

    // Crear nueva lista reordenada
    const updatedList = [...itemsInColumn];
    const [movedItem] = updatedList.splice(currentIndex, 1);
    updatedList.splice(validTargetIndex, 0, movedItem);

    const orderedList = applySequentialOrder(updatedList, columnId);

    debugLog('✅ Nueva lista:', updatedList.map((i, idx) => ({ 
        id: i.id, 
        currentOrder: i.order, 
        newOrder: idx + 1 
    })));

    // Actualizar UI y backend
    updateLocalColumn(columnId, orderedList);
    applyOrderUpdates(orderedList, columnId); // Sin await para no bloquear
    persistOrderSnapshot();
};

// Mover a otra columna
const moveToColumn = async (item, fromColumnId, toColumnId, toIndex) => {
    const sourceItems = getItemsInColumn(fromColumnId);
    const targetItems = getItemsInColumn(toColumnId);
    const currentIndex = sourceItems.findIndex(
        (current) => current.id === item.id && current.tipo === item.tipo
    );

    if (currentIndex === -1) return;

    debugLog('🔄 Moviendo entre columnas:', { 
        from: fromColumnId, 
        to: toColumnId, 
        toIndex,
        itemId: item.id,
        itemCategoryAntes: item.category
    });

    // Actualizar la categoría del item ANTES de insertarlo
    const itemWithNewCategory = {
        ...item,
        category: toColumnId,
        raw: item.raw ? { ...item.raw, category: toColumnId } : item.raw
    };

    const updatedSource = [...sourceItems];
    updatedSource.splice(currentIndex, 1);

    const targetPosition = Math.min(Math.max(toIndex, 0), targetItems.length);
    const updatedTarget = [...targetItems];
    updatedTarget.splice(targetPosition, 0, itemWithNewCategory);

    const orderedSource = applySequentialOrder(updatedSource, fromColumnId);
    const orderedTarget = applySequentialOrder(updatedTarget, toColumnId);

    debugLog('✅ Item con nueva categoría:', {
        id: itemWithNewCategory.id,
        categoryDespues: itemWithNewCategory.category
    });

    updateLocalColumn(fromColumnId, orderedSource);
    updateLocalColumn(toColumnId, orderedTarget);
    
    // Actualizar backend sin bloquear (sin await)
    applyOrderUpdates(orderedSource, fromColumnId);
    applyOrderUpdates(orderedTarget, toColumnId);
    persistOrderSnapshot();
};

// Calcula índice de inserción basado en la posición del puntero al soltar
const calculateDropIndex = (event, columnId) => {
    const cards = Array.from(
        event.currentTarget.querySelectorAll('.kanban-card')
    ).filter((el) => el.parentElement?.closest('.kanban-column')?.dataset?.col === columnId);

    if (cards.length === 0) {
        return 0;
    }

    const pointerY = event.clientY;
    for (let i = 0; i < cards.length; i++) {
        const rect = cards[i].getBoundingClientRect();
        const middle = rect.top + rect.height / 2;
        if (pointerY < middle) {
            return i;
        }
    }

    return cards.length; // al final
};

// Helper para aplicar órdenes y categoría de forma consistente
const applyOrderUpdates = async (items, columnId) => {
    // Actualiza primero el estado local para evitar saltos visuales, luego sincroniza con backend
    for (let i = 0; i < items.length; i++) {
        const currentItem = items[i];
        const endpoint = getEndpoint(currentItem.tipo, currentItem.id);
        if (!endpoint) continue;

        const orderValue = i + 1; // 1-based para evitar conflictos de backend
        const needsUpdate =
            currentItem.order !== orderValue || (currentItem.category || 'general-cases') !== columnId;

        if (!needsUpdate) continue;

        // Optimista: primero ajustamos local para que el DOM se reordene una sola vez
        currentItem.order = orderValue;
        currentItem.category = columnId;
        if (currentItem.raw) {
            currentItem.raw.order = orderValue;
            currentItem.raw.category = columnId;
        }

        // Sincroniza en serie para evitar carreras que dejen el orden anterior
        try {
            debugLog(`📤 Actualizando backend: ${currentItem.tipo} #${currentItem.id}`, {
                category: columnId,
                order: orderValue,
                endpoint
            });
            
            const response = await api.patch(endpoint, {
                category: columnId,
                order: orderValue
            });
            
            debugLog(`✅ Respuesta backend:`, response.data);
        } catch (error) {
            console.error(`❌ Error actualizando orden del item ${currentItem.id}`, error);
        }
    }
};

// Reemplaza los items de una columna en el estado local conservando el resto
const updateLocalColumn = (columnId, updatedList) => {
    const remaining = localItems.value.filter(
        (item) => (item.category || 'general-cases') !== columnId
    );
    localItems.value = [...remaining, ...updatedList];
};

// Guarda snapshot del orden actual en localStorage
const persistOrderSnapshot = () => {
    try {
        const payload = {};
        localItems.value.forEach((item) => {
            const key = `${item.tipo}-${item.id}`;
            payload[key] = {
                category: item.category || 'general-cases',
                order: item.order || 1
            };
        });
        localStorage.setItem(storageKey.value, JSON.stringify(payload));
    } catch (error) {
        console.error('No se pudo persistir orden local', error);
    }
};

// Helper para obtener el endpoint según el tipo
const getEndpoint = (tipo, id) => {
    const endpoints = {
        'nota-medica': `/patient-medical-notes/${id}/category`,
        'receta': `/patient-medical-prescriptions/${id}/category`,
        'documento': `/patient-documents/${id}/category`
    };
    return endpoints[tipo] || null;
};


// Utilidades
const mapTipo = (tipo) => {
    const map = {
        'nota-medica': 'Nota médica',
        'receta': 'Receta',
        'documento': 'Reporte'
    };
    return map[tipo] || tipo;
};

const getTypeIcon = (tipo) => {
    const icons = {
        'nota-medica': 'fas fa-file-medical',
        'receta': 'fas fa-prescription',
        'documento': 'fas fa-file-image'
    };
    return icons[tipo] || 'fas fa-file';
};

const formatFullDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('es-MX', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
    });
};

const editItem = (item) => {
    emit('edit-item', item);
};

const deleteItem = (item) => {
    emit('delete-item', item);
};
</script>

<style scoped>
.timeline-kanban {
    width: 100%;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 1.5rem;
    background: radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.08), transparent 30%),
        radial-gradient(circle at 90% 10%, rgba(14, 165, 233, 0.08), transparent 25%),
        linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
    position: relative;
}

.board-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
    padding: 0 0.25rem;
}

.eyebrow {
    margin: 0 0 0.15rem 0;
    color: #6366f1;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-size: 0.7rem;
}

.board-title {
    margin: 0;
    font-size: 1.25rem;
    color: #0f172a;
    letter-spacing: -0.01em;
}

.board-subtitle {
    margin: 0.15rem 0 0 0;
    color: #64748b;
    font-size: 0.85rem;
}

.kanban-board {
    display: flex;
    flex-wrap: wrap;
    gap: 1.1rem;
    align-items: stretch;
    justify-content: flex-start;
    min-height: 650px;
    padding-bottom: 1.5rem;
}

.kanban-column {
    flex: 1 1 320px;
    min-width: 300px;
    max-width: 360px;
    position: relative;
    background: rgba(255, 255, 255, 0.82);
    border-radius: 18px;
    box-shadow: 0 15px 40px rgba(15, 23, 42, 0.08), 0 2px 12px rgba(15, 23, 42, 0.04);
    display: flex;
    flex-direction: column;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(226, 232, 240, 0.8);
    backdrop-filter: blur(6px);
    overflow: hidden;
    isolation: isolate;
}

.kanban-column::after {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 18px;
    background: linear-gradient(120deg, rgba(99, 102, 241, 0.25), rgba(59, 130, 246, 0.18), rgba(192, 132, 252, 0.22));
    box-shadow: 0 18px 40px rgba(99, 102, 241, 0.12);
    opacity: 0;
    transition: opacity 0.25s ease, transform 0.25s ease;
    pointer-events: none;
    z-index: 0;
}

.kanban-column.is-active {
    border-color: rgba(99, 102, 241, 0.35);
    box-shadow: 0 18px 40px rgba(99, 102, 241, 0.18);
    transform: translateY(-3px);
}

.kanban-column.is-active::after {
    opacity: 1;
    transform: translateY(-1px);
}

.kanban-column:hover {
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1), 0 4px 14px rgba(15, 23, 42, 0.05);
    transform: translateY(-2px);
}

.column-header,
.column-body {
    position: relative;
    z-index: 1;
}

.column-header {
    padding: 0.875rem 1.25rem;
    background: linear-gradient(120deg, #2563eb, #7c3aed);
    border-radius: 18px 18px 0 0;
    position: relative;
    overflow: hidden;
}

.column-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    pointer-events: none;
}

.header-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    position: relative;
    z-index: 1;
}

.column-icon {
    font-size: 1.2rem;
    color: white;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.column-title {
    flex: 1;
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.025em;
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.column-count {
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    color: white;
    padding: 0.3rem 0.65rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 700;
    min-width: 26px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.column-body {
    flex: 1;
    padding: 1.25rem;
    overflow-y: auto;
    min-height: 250px;
    transition: background-color 0.3s ease;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.column-body.drag-over {
    background: radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.12), transparent 45%), linear-gradient(180deg, #e0f2fe 0%, #f8fafc 100%);
    border: 2px dashed rgba(59, 130, 246, 0.65);
    border-radius: 0 0 18px 18px;
    box-shadow: inset 0 0 24px rgba(59, 130, 246, 0.16);
    animation: columnGlow 0.8s ease-out;
}

.column-body::-webkit-scrollbar {
    width: 8px;
}

.column-body::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
    margin: 8px 0;
}

.column-body::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
    border-radius: 10px;
    border: 2px solid #f1f5f9;
    transition: all 0.2s ease;
}

.column-body::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}

.kanban-card {
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 12px;
    padding: 8px 10px 8px 28px;
    margin-bottom: 10px;
    cursor: grab;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
    position: relative;
    overflow: hidden;
}

.kanban-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #2563eb 0%, #7c3aed 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.kanban-card:hover {
    box-shadow: 0 18px 32px rgba(15, 23, 42, 0.15);
    transform: translateY(-4px) scale(1.01);
    border-color: #c7d2fe;
}

.kanban-card:hover::before {
    opacity: 1;
}

.kanban-card.dragging {
    opacity: 0.7;
    cursor: grabbing;
    transform: translateY(-2px) scale(0.985);
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.18), 0 0 0 3px rgba(99, 102, 241, 0.18), 0 14px 30px rgba(99, 102, 241, 0.16);
}

.drag-handle {
    position: absolute;
    left: 10px;
    top: 8px;
    color: #94a3b8;
    background: rgba(241, 245, 249, 0.85);
    border-radius: 10px;
    padding: 4px 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.kanban-card:hover .drag-handle {
    color: #1e3a8a;
}

.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
}

.card-type {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    white-space: nowrap;
}

.card-type i {
    font-size: 13px;
}

.type-label {
    font-weight: 700;
    letter-spacing: 0.02em;
}

.card-type.type-nota-medica {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1e40af;
    border: 1px solid #93c5fd;
}

.card-type.type-receta {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
    border: 1px solid #6ee7b7;
}

.card-type.type-documento {
    background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
    color: #9f1239;
    border: 1px solid #f9a8d4;
}



.card-body {
    margin-bottom: 4px;
}

.card-summary {
    font-size: 13px;
    color: #334155;
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    padding: 4px;
    background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
    border-left: 2px solid #e2e8f0;
    border-radius: 6px;
    font-weight: 500;
}

.card-empty {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
    padding: 4px;
    text-align: center;
    font-style: italic;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px dashed #cbd5e1;
}

.card-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-top: 4px;
    border-top: 1px solid #e2e8f0;
}

.card-date {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: #f59e0b;
    font-weight: 600;
}

.card-date i {
    font-size: 12px;
}

.card-actions {
    display: flex;
    gap: 6px;
    opacity: 0.6;
    transition: opacity 0.2s ease;
}

.kanban-card:hover .card-actions {
    opacity: 1;
}

.action-btn {
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    color: #64748b;
    padding: 0;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.action-btn:hover {
    transform: scale(1.1);
}

.action-btn:nth-child(1):hover {
    background: #dbeafe;
    color: #2563eb;
}

.action-btn:nth-child(2):hover {
    background: #fee2e2;
    color: #dc2626;
}

.empty-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 1.5rem;
    color: #94a3b8;
    text-align: center;
}

.empty-column i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.3;
    background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.empty-column p {
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 0;
    color: #64748b;
}

.column-body.drag-over .empty-column {
    border: 1px dashed rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    background: rgba(59, 130, 246, 0.06);
}

/* Estados de drag & drop */
.drop-indicator {
    position: relative;
    height: 28px;
    margin: 8px 0;
    padding: 6px 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    pointer-events: none;
    background: linear-gradient(120deg, rgba(99, 102, 241, 0.14), rgba(59, 130, 246, 0.12));
    border-radius: 10px;
    border: 1px dashed rgba(99, 102, 241, 0.45);
    box-shadow: 0 12px 30px rgba(99, 102, 241, 0.14);
    transition: opacity 0.18s ease, transform 0.18s ease, box-shadow 0.2s ease;
    z-index: 2;
}

.drop-line {
    width: 110px;
    height: 4px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7);
    border-radius: 999px;
    box-shadow: 0 2px 12px rgba(99, 102, 241, 0.5);
    position: relative;
    overflow: hidden;
    animation: pulseLine 1.4s ease-in-out infinite;
}

.drop-line::before,
.drop-line::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    background: #6366f1;
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.7);
}

.drop-line::after {
    right: -6px;
    animation: floatDot 1.4s ease-in-out infinite;
}

.drop-line::before {
    left: -6px;
}

.drop-text {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    color: #4f46e5;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    padding: 2px 6px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid rgba(99, 102, 241, 0.2);
    box-shadow: 0 2px 6px rgba(79, 70, 229, 0.14);
    animation: fadeUp 0.24s ease;
}

.drop-text i {
    font-size: 12px;
}

/* Animaciones para las tarjetas */
.card-list-move,
.card-list-enter-active,
.card-list-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-list-enter-from {
    opacity: 0;
    transform: translateY(-15px) scale(0.95);
}

.card-list-leave-to {
    opacity: 0;
    transform: translateY(15px) scale(0.95);
}

.card-list-leave-active {
    position: absolute;
    width: calc(100% - 2.5rem);
}

@keyframes pulseLine {
    0% {
        transform: scaleX(0.96);
        opacity: 0.85;
    }
    50% {
        transform: scaleX(1);
        opacity: 1;
    }
    100% {
        transform: scaleX(0.96);
        opacity: 0.85;
    }
}

@keyframes floatDot {
    0% { transform: translate(-1px, -50%); }
    50% { transform: translate(1px, -50%); }
    100% { transform: translate(-1px, -50%); }
}

@keyframes fadeUp {
    from { opacity: 0; transform: translateY(3px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes columnGlow {
    0% { box-shadow: inset 0 0 16px rgba(59, 130, 246, 0.08); }
    60% { box-shadow: inset 0 0 24px rgba(59, 130, 246, 0.18); }
    100% { box-shadow: inset 0 0 18px rgba(59, 130, 246, 0.14); }
}

/* Responsive */
@media (max-width: 1400px) {
    .kanban-column {
        min-width: 260px;
        max-width: 320px;
    }
    
    .column-header {
        padding: 1rem 1.25rem;
    }
    
    .column-icon {
        font-size: 1.25rem;
    }
    
    .column-title {
        font-size: 0.9375rem;
    }
}

@media (max-width: 1024px) {
    .timeline-kanban {
        padding: 1rem;
    }

    .board-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.6rem;
    }
    
    .kanban-board {
        overflow-x: auto;
        padding-bottom: 2rem;
        min-height: 550px;
    }
    
    .kanban-column {
        min-width: 280px;
        max-width: 280px;
    }
    
    .column-body {
        padding: 1rem;
    }
    
    .kanban-card {
        padding: 0.875rem;
    }

    .card-header {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (max-width: 768px) {
    .board-title {
        font-size: 1.15rem;
    }

    .board-subtitle {
        font-size: 0.88rem;
    }

    .kanban-column {
        min-width: 260px;
        max-width: 260px;
    }
    
    .column-header {
        padding: 0.875rem 1rem;
    }
    
    .column-title {
        font-size: 0.875rem;
    }
    
    .column-icon {
        font-size: 1.125rem;
    }
}
</style>
